/**
 * AnalyticsDashboard - Comprehensive analytics for admin dashboard
 * Revenue charts, popular items, customer insights, and activity feed
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, 
  Package, Clock, Calendar, BarChart3, PieChart, Activity,
  ArrowUpRight, ArrowDownRight, RefreshCw, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../../../utils/apiClient';

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [customerInsights, setCustomerInsights] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(dateRange);
      const [
        overviewRes,
        revenueRes,
        popularRes,
        categoryRes,
        hourlyRes,
        customersRes,
        activityRes
      ] = await Promise.all([
        api.get(`/admin/analytics/overview?days=${days}`),
        api.get(`/admin/analytics/revenue-chart?days=${days}`),
        api.get(`/admin/analytics/popular-items?days=${days}`),
        api.get(`/admin/analytics/category-breakdown?days=${days}`),
        api.get(`/admin/analytics/hourly-orders?days=${days}`),
        api.get(`/admin/analytics/customer-insights?days=${days}`),
        api.get('/admin/analytics/recent-activity')
      ]);

      setOverview(overviewRes.data);
      setRevenueChart(revenueRes.data);
      setPopularItems(popularRes.data);
      setCategoryData(categoryRes.data);
      setHourlyData(hourlyRes.data);
      setCustomerInsights(customersRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Format currency
  const formatCurrency = (value) => `$${value?.toFixed(2) || '0.00'}`;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get max value for chart scaling
  const maxRevenue = Math.max(...revenueChart.map(d => d.revenue), 1);
  const maxHourly = Math.max(...hourlyData.map(d => d.orders), 1);
  const totalCategoryRevenue = categoryData.reduce((sum, c) => sum + c.revenue, 0);

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Analytics Dashboard</h2>
          <p className="text-muted-foreground">Track your business performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]" data-testid="date-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Net Revenue"
            value={formatCurrency(overview.total_revenue)}
            change={overview.revenue_growth}
            icon={DollarSign}
            color="green"
            subtitle="What cafe keeps"
          />
          <StatCard
            label="Total Orders"
            value={overview.total_orders}
            change={overview.orders_growth}
            icon={ShoppingCart}
            color="blue"
          />
          <StatCard
            label="Avg Order Value"
            value={formatCurrency(overview.avg_order_value)}
            icon={BarChart3}
            color="purple"
            subtitle="Net per order"
          />
          <StatCard
            label="VAT Collected"
            value={formatCurrency(overview.vat_collected || 0)}
            icon={Activity}
            color="orange"
            subtitle="12.5% owed to FRCS"
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Revenue Trend
          </h3>
          <div className="h-[200px] flex items-end gap-1">
            {revenueChart.slice(-14).map((day, i) => (
              <div 
                key={day.date}
                className="flex-1 flex flex-col items-center gap-1"
              >
                <div 
                  className="w-full bg-primary/80 rounded-t hover:bg-primary transition-colors cursor-pointer group relative"
                  style={{ height: `${(day.revenue / maxRevenue) * 100}%`, minHeight: '4px' }}
                  title={`${formatDate(day.date)}: ${formatCurrency(day.revenue)}`}
                >
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {formatCurrency(day.revenue)}
                  </div>
                </div>
                {i % 2 === 0 && (
                  <span className="text-[10px] text-muted-foreground">{formatDate(day.date)}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Distribution */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Peak Hours
          </h3>
          <div className="h-[200px] flex items-end gap-0.5">
            {hourlyData.map((hour) => (
              <div 
                key={hour.hour}
                className="flex-1 flex flex-col items-center"
              >
                <div 
                  className={`w-full rounded-t transition-colors cursor-pointer ${
                    hour.orders > maxHourly * 0.7 ? 'bg-primary' :
                    hour.orders > maxHourly * 0.4 ? 'bg-primary/60' :
                    'bg-primary/30'
                  }`}
                  style={{ height: `${(hour.orders / maxHourly) * 100}%`, minHeight: '2px' }}
                  title={`${hour.label}: ${hour.orders} orders`}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>12am</span>
            <span>6am</span>
            <span>12pm</span>
            <span>6pm</span>
            <span>12am</span>
          </div>
        </div>
      </div>

      {/* Popular Items & Categories */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Popular Items */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Top Selling Items
          </h3>
          <div className="space-y-3">
            {popularItems.slice(0, 5).map((item, i) => (
              <div key={item.item_id} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-700' :
                  i === 1 ? 'bg-gray-100 text-gray-700' :
                  i === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {i + 1}
                </span>
                {item.image ? (
                  <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-muted" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.count} sold</p>
                </div>
                <span className="text-sm font-medium text-primary">{formatCurrency(item.revenue)}</span>
              </div>
            ))}
            {popularItems.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No sales data yet</p>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary" />
            Revenue by Category
          </h3>
          <div className="space-y-3">
            {categoryData.slice(0, 6).map((cat) => {
              const percentage = totalCategoryRevenue > 0 
                ? (cat.revenue / totalCategoryRevenue * 100).toFixed(1) 
                : 0;
              return (
                <div key={cat.category_id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground">{cat.category_name}</span>
                    <span className="text-muted-foreground">{formatCurrency(cat.revenue)} ({percentage}%)</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {categoryData.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No category data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Customer Insights & Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Customer Insights */}
        {customerInsights && (
          <div className="bg-card rounded-xl p-6 border border-border">
            <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Customer Insights
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-foreground">{customerInsights.total_customers}</p>
                <p className="text-xs text-muted-foreground">Total Customers</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-2xl font-bold text-foreground">{customerInsights.repeat_rate}%</p>
                <p className="text-xs text-muted-foreground">Repeat Rate</p>
              </div>
            </div>
            <h4 className="text-sm font-medium text-foreground mb-2">Top Customers</h4>
            <div className="space-y-2">
              {customerInsights.top_customers?.slice(0, 5).map((customer) => (
                <div key={`customer-${customer.customer_name}-${customer.order_count}`} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate">{customer.customer_name}</span>
                  <span className="text-muted-foreground">{customer.order_count} orders • {formatCurrency(customer.total_spend)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Recent Activity
          </h3>
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            {recentActivity.map((activity, i) => (
              <div key={`activity-${activity.type}-${activity.time}-${i}`} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activity.type === 'order' ? 'bg-green-100' :
                  activity.type === 'stock' ? 'bg-blue-100' :
                  'bg-purple-100'
                }`}>
                  {activity.type === 'order' && <ShoppingCart className="w-4 h-4 text-green-600" />}
                  {activity.type === 'stock' && <Package className="w-4 h-4 text-blue-600" />}
                  {activity.type === 'message' && <Users className="w-4 h-4 text-purple-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{activity.message}</p>
                  <p className="text-xs text-muted-foreground">{formatTime(activity.timestamp)}</p>
                </div>
                {activity.amount && (
                  <span className="text-sm font-medium text-primary">{formatCurrency(activity.amount)}</span>
                )}
              </div>
            ))}
            {recentActivity.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, change, icon: Icon, color, subtitle }) {
  const colorClasses = {
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
  };

  return (
    <div className="bg-card rounded-xl p-4 border border-border">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subtitle && <p className="text-xs text-muted-foreground/70 mt-0.5">{subtitle}</p>}
    </div>
  );
}
